/**
 * CRUD cho danh sách ứng dụng trên Realtime Database.
 *
 * Đọc là public (theo database.rules.json), ghi yêu cầu token của admin.
 */

import { APPS_DB_PATH, FIREBASE_CONFIG } from '../config/site';
import { seedApps } from '../data/seedApps';
import { normalizeApp, sortApps } from '../utils/appSchema';

const dbUrl = (path, token) =>
    `${FIREBASE_CONFIG.databaseURL}${path}.json${token ? `?auth=${token}` : ''}`;

/**
 * Chỉ các hàm GHI mới cần Firebase Auth. Import động để SDK auth nằm trong
 * chunk riêng — khách vào trang chủ không phải tải nó về.
 */
const requireToken = async () => {
    const { getAuthToken } = await import('./authService');
    const token = await getAuthToken();
    if (!token) throw new Error('Bạn chưa đăng nhập. Vui lòng đăng nhập lại.');
    return token;
};

/** Lấy các bản ghi đang có trên Firebase (chưa gộp với seed). */
export const fetchRemoteApps = async () => {
    try {
        const response = await fetch(dbUrl(APPS_DB_PATH));
        if (!response.ok) throw new Error('Không tải được dữ liệu từ Firebase');
        const data = await response.json();
        if (!data) return [];
        return Object.entries(data).map(([key, value]) => normalizeApp({ ...value, key }));
    } catch (error) {
        console.error('Lỗi khi tải apps từ Firebase:', error);
        return [];
    }
};

/** Seed local đã chuẩn hoá, đánh dấu `isSeed` để admin biết chưa có trên DB. */
export const getSeedApps = () =>
    seedApps.map((app) => normalizeApp({ ...app, isSeed: true }));

/**
 * Toàn bộ ứng dụng = bản ghi trên Firebase + seed chưa được ghi đè.
 * Dùng cho cả trang công khai lẫn trang quản trị.
 */
export const fetchAllApps = async () => {
    const remote = await fetchRemoteApps();
    const remoteIds = new Set(remote.map((app) => app.id));
    const seedOnly = getSeedApps().filter((app) => !remoteIds.has(app.id));
    return sortApps([...remote, ...seedOnly]);
};

/** Chỉ những ứng dụng người dùng được thấy. */
export const fetchPublicApps = async () => {
    const all = await fetchAllApps();
    return all.filter((app) => !app.hidden);
};

/** Tạo mới hoặc cập nhật. Trả về key của bản ghi trên Firebase. */
export const saveApp = async (appData, existingKey = null) => {
    const token = await requireToken();
    const { key, isSeed, ...payload } = appData;

    if (existingKey) {
        const response = await fetch(dbUrl(`${APPS_DB_PATH}/${existingKey}`, token), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error('Lỗi khi cập nhật ứng dụng');
        return { key: existingKey };
    }

    const response = await fetch(dbUrl(APPS_DB_PATH, token), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error('Lỗi khi thêm ứng dụng');
    const result = await response.json();
    return { key: result.name };
};

/** Cập nhật một vài trường (ẩn/hiện, nổi bật, thứ tự...) mà không đụng phần còn lại. */
export const patchApp = async (key, changes) => {
    const token = await requireToken();
    const response = await fetch(dbUrl(`${APPS_DB_PATH}/${key}`, token), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(changes),
    });
    if (!response.ok) throw new Error('Lỗi khi cập nhật ứng dụng');
};

export const deleteApp = async (key) => {
    const token = await requireToken();
    const response = await fetch(dbUrl(`${APPS_DB_PATH}/${key}`, token), { method: 'DELETE' });
    if (!response.ok) throw new Error('Lỗi khi xóa ứng dụng');
};
